import React, { FC } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Markdown from './Markdown';
import MediaGallery from './MediaGallery';
import LinkPreviewCard from './LinkPreviewCard';
import InAppPostEmbed from './InAppPostEmbed';
import { parseBodyLinks } from '../utils/inAppPostLink';
import type { MediaItem, LinkPreview, MediaLayout } from '../types/common';

interface PostContentProps {
  textSubmission?: string;
  media?: MediaItem[];
  links?: LinkPreview[];
  mediaLayout?: MediaLayout;
  /** Optional cap on media preview height (e.g. the mobile media height). */
  mediaMaxHeight?: string | number;
}

/**
 * The full body of a post — Markdown text, the media gallery (carousel/grid),
 * and link-preview cards. Shared by the expanded post card and the post detail
 * page so the rendering stays identical everywhere.
 */
const PostContent: FC<PostContentProps> = ({
  textSubmission,
  media = [],
  links = [],
  mediaLayout = 'carousel',
  mediaMaxHeight,
}) => {
  const theme = useTheme();

  // Standalone links in the body unfurl: in-app posts → embeds, external URLs →
  // preview cards. Inline URLs stay in the Markdown text as normal links.
  const { text: bodyText, inAppPostIds, externalUrls } = parseBodyLinks(textSubmission);
  // Server-fetched OG data (title/image) keyed by url; falls back to a bare card.
  const linkByUrl = new Map((links || []).map((l) => [l.url, l]));

  return (
    <>
      {bodyText && (
        <Box
          sx={{
            mb: 2,
            // Guide .post .bd: body copy sits one step down the text hierarchy.
            color: theme.tokens.colors.text2,
            fontSize: theme.tokens.typography.sizes.postBody,
            lineHeight: theme.tokens.typography.lineHeights.postBody,
            wordBreak: 'break-word',
          }}
        >
          <Markdown>{bodyText}</Markdown>
        </Box>
      )}

      {inAppPostIds.map((postId) => (
        <InAppPostEmbed key={postId} id={postId} />
      ))}

      <MediaGallery media={media} layout={mediaLayout} maxHeight={mediaMaxHeight} />

      {externalUrls.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: inAppPostIds.length || media.length ? 1.5 : 0, mb: 2 }}>
          {externalUrls.map((url) => (
            <LinkPreviewCard key={url} link={(linkByUrl.get(url) as LinkPreview) || ({ url } as LinkPreview)} />
          ))}
        </Box>
      )}
    </>
  );
};

export default PostContent;
